import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'

import { isAvailableFeatureSelector } from 'features/billing'
import FeatureFlags from 'services/feature_flags'

import { getVisibleActions } from './actions'
import {
  apiEventTypesWorkflowUrl,
  apiManagedWorkflowsUrl,
  apiOwnerListUrl,
} from './url'

export const SMS_TO_INVITEE_WORKFLOW_ACTION_TYPE = 'booking.sms.event_to_invitee'

const SMS_TO_INVITEE_TITLE_KEYS = new Set([
  'publisher.workflows.actions.booking.sms.event_to_invitee.title',
  'workflows.actions.booking.sms.event_to_invitee.title',
])

const smsToInviteeActionTitle = () => (
  I18n.t('publisher.workflows.actions.booking.sms.event_to_invitee.title')
)

/** Event-type workflow list responses often expose action titles, not full configuration. */
export const workflowActionHasSmsToInvitee = (action) => {
  if (action?.configuration?.type === SMS_TO_INVITEE_WORKFLOW_ACTION_TYPE) return true

  const title = action?.title
  if (!title) return false

  if (title === SMS_TO_INVITEE_WORKFLOW_ACTION_TYPE) return true
  if (SMS_TO_INVITEE_TITLE_KEYS.has(title)) return true

  return title === smsToInviteeActionTitle()
}

export const workflowHasSmsToInviteeAction = (workflow) => (
  getVisibleActions(workflow?.workflow_rule?.workflow_actions ?? []).some(workflowActionHasSmsToInvitee)
)

export const workflowsHaveSmsToInviteeAction = (workflows) => (
  (workflows ?? []).some(workflowHasSmsToInviteeAction)
)

const getWorkflowsOwner = ({ ownerType, ownerId, creatorId }) => {
  if (ownerType === 'Organization') {
    return { id: creatorId, type: 'user' }
  }

  const resolvedOwnerId = ownerType === 'User' && !ownerId ? 'me' : ownerId

  return { id: resolvedOwnerId, type: ownerType }
}

const filterManagedWorkflowsForEventType = (collection, eventTypeId, parentId) => (
  (collection ?? []).filter((workflow) => {
    if (!workflow.event_types || !Array.isArray(workflow.event_types)) return false

    return workflow.event_types.some((eventType) => {
      if (parentId === null && eventType.id === null) return false

      return String(eventType.id) === String(eventTypeId) || String(eventType.id) === String(parentId)
    })
  })
)

export const fetchWorkflowsForBookModalEventType = async ({
  eventType,
  eventTypeType,
  eventTypeOwnerType,
  eventTypeOwnerId,
  getState,
}) => {
  const isWorkflowsAvailable = isAvailableFeatureSelector(getState(), { featureKey: 'workflows' })
  if (!isWorkflowsAvailable || !eventType?.id) return []

  if (eventType.isMETParent) {
    const [success, data] = await httpRequest({ method: 'GET', url: apiManagedWorkflowsUrl })
    if (!success || data?.type === 'policy') return []

    return filterManagedWorkflowsForEventType(data.collection, eventType.id, eventType.parent_id)
  }

  const workflowsExtractionEnabled = FeatureFlags.isEnabled('raven_workflows_extraction')
  const { id: workflowOwnerId, type: workflowOwnerType } = getWorkflowsOwner({
    ownerType: eventTypeOwnerType,
    ownerId: eventTypeOwnerId,
    creatorId: eventType.creator_id,
  })

  if (workflowsExtractionEnabled) {
    const [success, data] = await httpRequest({
      method: 'GET',
      url: apiOwnerListUrl,
      params: {
        owner_id: workflowOwnerId,
        owner_type: workflowOwnerType.toLowerCase(),
        event_type_id: eventType.id,
      },
    })
    if (!success || data?.type === 'policy') return []

    return data.collection?.[0]?.workflows?.collection ?? []
  }

  const [success, data] = await httpRequest({
    method: 'GET',
    url: apiEventTypesWorkflowUrl(
      eventTypeType,
      eventTypeOwnerType,
      eventTypeOwnerType === 'User' && !eventTypeOwnerId ? 'me' : eventTypeOwnerId,
      eventType.id,
    ),
  })
  if (!success || data?.type === 'policy') return []

  return data.workflows?.collection ?? []
}
